import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import poofAnimation from "../assets/poof2.json";
import EmblaCarousel from "../components/EmblaCarousel";
import FavoritesContextMenu from "../components/FavoritesContextMenu";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import MoviesCarousel from "../components/MoviesCarousel";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import RecentlyWatchedCarousel from "../components/RecentlyWatchedCarousel";
import SeriesCarousel from "../components/SeriesCarousel";
import WeatherWidget from "../components/WeatherWidget";
import useFontSize from "../hooks/useFontSize";
import apiService from "../services/api";
import webPlayerHeartbeatService from "../services/webPlayerHeartbeat";
import "./WebPlayerHome.css";
import { debugLog } from "../utils/debugLog";
import webPlayerStorage from "../utils/webPlayerStorage";

const clockDuration = (value) => {
  if (value == null || value === "") return "";

  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

    if (match) {
      const hours = match[1].padStart(2, "0");
      const minutes = match[2];
      const seconds = match[3] || "00";

      if (hours === "00" && minutes === "00" && seconds === "00") return "";

      return `${hours}:${minutes}:${seconds}`;
    }
  }

  const hourMatch = String(value).match(/(\d+)\s*h/i);
  const minMatch = String(value).match(/(\d+)\s*m/i);

  if (hourMatch || minMatch) {
    const hours = hourMatch ? Number(hourMatch[1]) : 0;
    const mins = minMatch ? Number(minMatch[1]) : 0;

    if (hours <= 0 && mins <= 0) return "";

    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
  }

  let minutes = Number(value);

  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  // Episode lengths sometimes arrive as seconds.
  if (minutes > 1000) minutes = Math.round(minutes / 60);

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
};

const episodeClock = (episode) =>
  clockDuration(
    episode?.duration ||
      episode?.runtime ||
      episode?.runTime ||
      episode?.length ||
      episode?.episode_run_time
  );

const seriesWatchTime = (series) => {
  const direct = clockDuration(series?.duration);

  if (direct) return direct;

  const episodes = [];

  if (Array.isArray(series?.seasons)) {
    [...series.seasons]
      .filter((season) => season?.season !== 0)
      .sort((a, b) => (a.season || 0) - (b.season || 0))
      .forEach((season) => {
        if (Array.isArray(season.episodes)) episodes.push(...season.episodes);
      });
  }

  if (Array.isArray(series?.episodes)) {
    episodes.push(
      ...series.episodes.filter((episode) => episode?.season !== 0)
    );
  }

  episodes.sort(
    (a, b) =>
      (a.season || 0) - (b.season || 0) || (a.episode || 0) - (b.episode || 0)
  );

  const runTimes = series?.episode_run_time || series?.episodeRunTime;

  if (Array.isArray(runTimes)) {
    runTimes.forEach((minutes) => episodes.push({ duration: minutes }));
  }

  for (const episode of episodes) {
    const duration = episodeClock(episode);

    if (duration) return duration;
  }

  return "";
};

const fillSeriesWatchTimes = async (items, setItems) => {
  const pending = items.filter(
    (item) =>
      (item.mediaType === "tv" || item.mediaType === "series") &&
      !item.duration &&
      item.mediaId &&
      item.mediaId !== -1
  );

  if (!pending.length) return;

  const updates = await Promise.all(
    pending.map(async (item) => {
      try {
        const response = await apiService.webPlayerGetSeriesDetails(
          item.mediaId
        );
        const series = response?.data || response;

        return [String(item.mediaId), seriesWatchTime(series)];
      } catch {
        return [String(item.mediaId), ""];
      }
    })
  );
  const byId = new Map(updates.filter(([, duration]) => duration));

  if (!byId.size) return;

  setItems((current) => {
    const next = current.map((item) =>
      byId.has(String(item.mediaId))
        ? { ...item, duration: byId.get(String(item.mediaId)) }
        : item
    );

    sessionStorage.setItem("home_carousel", JSON.stringify(next));

    return next;
  });
};

// Memoized Recently Watched Section Component
const RecentlyWatchedSection = memo(
  ({ items, onItemClick, onItemRemove }) => {
    debugLog("RecentlyWatchedSection rendering with items:", items.length);

    return (
      <section
        className="mb-8 overflow-visible section-content"
        style={{ minHeight: "200px" }}
      >
        <h2 className="text-xl sm:text-2xl font-semibold text-white pb-3.5">
          Recently Watched TV
        </h2>
        <div
          className="py-4"
          style={{ overflow: "visible", minHeight: "120px" }}
        >
          <RecentlyWatchedCarousel
            items={items.slice(0, 10)}
            showEpg={true}
            showLogo={true}
            showProgress={true}
            viewMoreHref={items.length >= 10 ? "/web-player/watched-tv" : null}
            onItemClick={onItemClick}
            onItemRemove={onItemRemove}
          />
        </div>
      </section>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if items actually changed
    if (prevProps.items.length !== nextProps.items.length) {
      return false;
    }

    const prevIds = prevProps.items
      .map((item) => item.mediaId || item.id || item.stream_id)
      .join(",");
    const nextIds = nextProps.items
      .map((item) => item.mediaId || item.id || item.stream_id)
      .join(",");

    // Also check callbacks haven't changed
    if (
      prevProps.onItemClick !== nextProps.onItemClick ||
      prevProps.onItemRemove !== nextProps.onItemRemove
    ) {
      return false;
    }

    return prevIds === nextIds;
  }
);

// Memoized Continue Watching Section Component
const ContinueWatchingSection = memo(
  ({ items, onItemClick, onItemRemove, onViewDetails }) => {
    debugLog("ContinueWatchingSection rendering with items:", items.length);
    debugLog(
      "[DEBUG] ContinueWatchingSection items:",
      items.map((i) => ({
        mediaId: i.mediaId,
        id: i.id,
        progress: i.progress,
        name: i.name || i.title,
      }))
    );

    const [contextMenuItem, setContextMenuItem] = React.useState(null);
    const [bookmarkedItems, setBookmarkedItems] = React.useState({});
    const [deletingItems, setDeletingItems] = React.useState({});
    const longPressTimers = React.useRef({});

    // Cleanup timers on unmount
    React.useEffect(
      () => () => {
        Object.values(longPressTimers.current).forEach((timer) =>
          clearTimeout(timer)
        );
      },
      []
    );

    // Map items to match MoviesCarousel/SeriesCarousel format
    const mappedItems = items.map((item) => {
      // For series, use episodeInfo from metadata if available, otherwise use name
      const episodeInfo = item.metadata?.episodeInfo || item.episodeInfo;
      const displayName =
        item.mediaType === "series" && episodeInfo
          ? episodeInfo
          : item.name || item.title;

      return {
        ...item,
        mediaId: item.mediaId || item.id,
        posterPath: item.posterPath || item.poster,
        name: displayName,
        title: displayName,
      };
    });

    const handleClick = (item) => {
      if (onItemClick) {
        onItemClick(item);
      }
    };

    // Handle long press start
    const handleLongPressStart = (item, e) => {
      e.preventDefault();
      const itemId = item.mediaId || item.id;
      const timerId = setTimeout(() => {
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        setContextMenuItem(item);
      }, 500);

      longPressTimers.current[itemId] = timerId;
    };

    // Handle long press end
    const handleLongPressEnd = (itemId) => {
      if (longPressTimers.current[itemId]) {
        clearTimeout(longPressTimers.current[itemId]);
        delete longPressTimers.current[itemId];
      }
    };

    // Handle play now
    const handlePlayNow = (item) => {
      setContextMenuItem(null);
      if (onItemClick) {
        onItemClick(item);
      }
    };

    // Handle mark as watched (remove from continue watching)
    const handleMarkAsWatched = async (item) => {
      const itemId = item.mediaId || item.id;

      try {
        // Mark item as being deleted for animation
        setDeletingItems((prev) => ({ ...prev, [itemId]: true }));
        setContextMenuItem(null);

        // Call API to mark as watched
        const mediaId = String(item.mediaId || item.id).trim();
        const mediaType = item.mediaType || item.type || "movie";

        await apiService.webPlayerMarkAsWatched(mediaId, mediaType);

        // Play swoosh sound
        const swooshSound = require("../assets/swoosh.mp3");
        const audio = new Audio(swooshSound);

        audio.play().catch((e) => debugLog("Audio play failed:", e));

        // Wait for animation
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Clean up deleting state
        setDeletingItems((prev) => {
          const newState = { ...prev };

          delete newState[itemId];

          return newState;
        });

        // Remove from continue watching
        if (onItemRemove) {
          onItemRemove(item);
        }

        toast.success("Marked as watched");
      } catch (error) {
        console.error("Error marking as watched:", error);
        toast.error("Failed to mark as watched");
        setDeletingItems((prev) => {
          const newState = { ...prev };

          delete newState[itemId];

          return newState;
        });
      }
    };

    // Handle add to watchlist
    const handleAddToWatchlist = async (item) => {
      try {
        const mediaId = String(item.mediaId || item.id).trim();
        const mediaType = item.mediaType || item.type || "movie";
        const itemId = item.mediaId || item.id;
        const isCurrentlyBookmarked =
          bookmarkedItems[itemId] || item.isBookmarked;

        if (isCurrentlyBookmarked) {
          // Remove from watchlist
          await apiService.webPlayerDeleteBookmark(mediaId, mediaType);
          setBookmarkedItems((prev) => ({ ...prev, [itemId]: false }));
          toast.success("Removed from favorites");
        } else {
          // Add to watchlist
          await apiService.webPlayerCreateBookmark(mediaId, mediaType);
          setBookmarkedItems((prev) => ({ ...prev, [itemId]: true }));
          toast.success("Added to favorites");
        }

        setContextMenuItem(null);
      } catch (error) {
        console.error("Error updating watchlist:", error);
        toast.error(error.message || "Failed to update favorites");
        setContextMenuItem(null);
      }
    };

    // Handle view details
    const handleViewDetails = (item) => {
      setContextMenuItem(null);
      if (onViewDetails) {
        onViewDetails(item);
      }
    };

    return (
      <section
        className="mb-8 overflow-visible section-content"
        style={{ minHeight: "200px" }}
      >
        <h2 className="text-xl sm:text-2xl font-semibold text-white pb-3.5">
          Continue Watching
        </h2>
        <div
          className="py-4"
          style={{
            overflow: "visible",
            minHeight: "120px",
          }}
        >
          <div
            className="home-edge-row select-none"
            style={{
              paddingTop: 10,
              paddingBottom: 10,
              overflowX: "clip",
              overflowY: "visible",
              width: "100%",
            }}
          >
            <div className="flex gap-4" style={{ overflow: "visible" }}>
              {mappedItems.map((item, idx) => {
                const key =
                  item.mediaId ??
                  item.id ??
                  item.stream_id ??
                  item.title ??
                  idx;
                const image = item.posterPath || item.poster || item.thumbnail;
                const isLastItem = idx === mappedItems.length - 1;
                const itemId = item.mediaId || item.id;
                const isDeleting = deletingItems[itemId];
                const isBookmarked =
                  bookmarkedItems[itemId] !== undefined
                    ? bookmarkedItems[itemId]
                    : item.isBookmarked;

                if (isDeleting) {
                  return null;
                }

                return (
                  <motion.div
                    className={`movies-fixed-item flex-shrink-0 w-[calc((100vw-48px)/2)] sm:w-[192px] md:w-[216px] lg:w-[240px] max-[932px]:landscape:w-[calc((100vw-96px)/4.5)] ${isLastItem ? "mr-16 lg:mr-32" : ""} cursor-pointer`}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      transition: { duration: 0.3 },
                    }}
                    initial={false}
                    key={key}
                    onClick={() => !contextMenuItem && handleClick(item)}
                    onMouseDown={(e) => handleLongPressStart(item, e)}
                    onMouseLeave={() => handleLongPressEnd(itemId)}
                    onMouseUp={() => handleLongPressEnd(itemId)}
                    onTouchEnd={() => handleLongPressEnd(itemId)}
                    onTouchStart={(e) => handleLongPressStart(item, e)}
                  >
                    <article className="transform-gpu">
                      <div className="relative group">
                        <div
                          className="lunova-card relative card-radius-poster"
                          style={{
                            aspectRatio: "2 / 3",
                            transformOrigin: "center center",
                            boxShadow:
                              "0 15px 35px -10px rgba(0,0,0,0.75), " +
                              "0 10px 20px -5px rgba(0,0,0,0.5)",
                          }}
                        >
                          <div className="lunova-card-clip">
                            {image ? (
                              <img
                                alt={item.name || item.title || "Content"}
                                className="w-full h-full object-cover"
                                draggable={false}
                                src={image}
                                onDragStart={(e) => e.preventDefault()}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.parentElement.innerHTML = `<div class="flex items-center justify-center text-white text-lg font-bold p-4 text-center h-full" style="background: linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)">${
                                    item.name || item.title || "Content"
                                  }</div>`;
                                }}
                              />
                            ) : (
                              <div
                                className="flex items-center justify-center text-white text-lg font-bold p-4 text-center h-full"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                                }}
                              >
                                {item.name || item.title || "Content"}
                              </div>
                            )}

                            {/* Progress Bar - Always show, even at 0% */}
                            <div className="absolute bottom-3 left-0 right-0 flex justify-center px-4 z-10">
                              <div
                                className="h-2 bg-gray-600 rounded-full overflow-hidden"
                                style={{ width: "80%" }}
                              >
                                <div
                                  className="h-full rounded-full transition-[width] duration-300"
                                  style={{
                                    backgroundColor: "#6495ED",
                                    width: `${Math.min(Math.max(0, item.progress || 0), 100)}%`,
                                  }}
                                  title={`Progress: ${Math.round(item.progress || 0)}%`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bookmark Star Indicator - Bottom Right */}
                        {isBookmarked && (
                          <div className="absolute bottom-8 right-2 z-20">
                            <svg
                              className="w-6 h-6 text-yellow-400 drop-shadow-lg"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </div>
                        )}

                        {/* Poof Animation Overlay */}
                        {isDeleting && (
                          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
                            <Lottie
                              animationData={poofAnimation}
                              loop={false}
                              style={{ width: 200, height: 200 }}
                            />
                          </div>
                        )}

                        {/* Title below the card */}
                        <div className="text-white text-sm font-bold truncate text-center mt-2 px-1 select-none">
                          {item.name || item.title || "Content"}
                        </div>
                      </div>
                    </article>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Full-screen Context Menu Modal */}
        <AnimatePresence>
          {contextMenuItem &&
            (() => {
              const mt =
                contextMenuItem.mediaType || contextMenuItem.type || "movie";
              const isLive = [
                "live-tv",
                "live",
                "livetv",
                "tv-channel",
              ].includes(mt);
              const ctxType = isLive
                ? "tv"
                : mt === "series"
                  ? "series"
                  : "movie";
              const favKey = contextMenuItem.mediaId || contextMenuItem.id;
              const isFav =
                bookmarkedItems[favKey] !== undefined
                  ? bookmarkedItems[favKey]
                  : contextMenuItem.isBookmarked;
              const isMovieOrSeries = mt === "movie" || mt === "series";

              return (
                <FavoritesContextMenu
                  actions={[
                    {
                      key: "play",
                      icon: "fa-play",
                      label: "Play Now",
                      onClick: () => handlePlayNow(contextMenuItem),
                    },
                    {
                      key: "watched",
                      icon: "fa-check",
                      label: "Mark as Watched",
                      onClick: () => handleMarkAsWatched(contextMenuItem),
                    },
                    {
                      key: "fav",
                      icon: "fa-bookmark",
                      label: isFav
                        ? "Clear from Favorites"
                        : "Add to Favorites",
                      onClick: () => handleAddToWatchlist(contextMenuItem),
                    },
                    ...(isMovieOrSeries
                      ? [
                          {
                            key: "details",
                            icon: "fa-circle-info",
                            label: "View Details",
                            onClick: () => handleViewDetails(contextMenuItem),
                          },
                        ]
                      : []),
                    {
                      key: "remove",
                      icon: "fa-trash",
                      label: "Remove",
                      danger: true,
                      onClick: () => onItemRemove(contextMenuItem),
                    },
                  ]}
                  item={contextMenuItem}
                  type={ctxType}
                  onClose={() => setContextMenuItem(null)}
                />
              );
            })()}
        </AnimatePresence>
      </section>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if items actually changed
    if (prevProps.items.length !== nextProps.items.length) {
      return false;
    }

    const prevIds = prevProps.items
      .map((item) => item.mediaId || item.id || item.stream_id)
      .join(",");
    const nextIds = nextProps.items
      .map((item) => item.mediaId || item.id || item.stream_id)
      .join(",");

    // Also check if progress values changed
    const prevProgress = prevProps.items
      .map((item) => item.progress || 0)
      .join(",");
    const nextProgress = nextProps.items
      .map((item) => item.progress || 0)
      .join(",");

    // Also check callbacks haven't changed
    if (prevProps.onItemClick !== nextProps.onItemClick) {
      return false;
    }

    return prevIds === nextIds && prevProgress === nextProgress;
  }
);

const WebPlayerHome = () => {
  useFontSize(); // Apply font size settings
  const navigate = useNavigate();
  const location = useLocation();
  const hasShownRecentlyWatched = useRef(false);

  // Check if user is authenticated
  const isAuthenticated = !!webPlayerStorage.getToken();

  // Get profile and playlist from navigation state - Memoize to prevent infinite loops
  const profile = useMemo(
    () =>
      location.state?.profile ||
      JSON.parse(sessionStorage.getItem("selectedProfile") || "{}"),
    [location.state?.profile]
  );
  const playlist = useMemo(
    () => location.state?.playlist || {},
    [location.state?.playlist]
  );
  const playlistId = playlist?.id || localStorage.getItem("currentPlaylistId");

  // Beta banner state - show once per session
  const [showBetaBanner, setShowBetaBanner] = useState(() => {
    const dismissedSession = sessionStorage.getItem("betaBannerDismissed");

    return dismissedSession !== "true";
  });

  // State - Initialize with cached data to prevent empty state
  const [carouselItems, setCarouselItems] = useState(() => {
    const cached = sessionStorage.getItem("home_carousel");

    return cached ? JSON.parse(cached) : [];
  });
  const [recentlyWatched, setRecentlyWatched] = useState(() => {
    const cached = sessionStorage.getItem("home_recently_watched");

    return cached ? JSON.parse(cached) : [];
  });
  const [continueWatching, setContinueWatching] = useState(() => {
    const cached = sessionStorage.getItem("home_continue_watching");

    return cached ? JSON.parse(cached) : [];
  });
  const [movies, setMovies] = useState(() => {
    const cached = sessionStorage.getItem("home_movies");

    return cached ? JSON.parse(cached) : [];
  });
  const [series, setSeries] = useState(() => {
    const cached = sessionStorage.getItem("home_series");

    return cached ? JSON.parse(cached) : [];
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Video player state - exactly like watchlist
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [currentPlayingItem, setCurrentPlayingItem] = useState(null);

  useEffect(() => {
    // Only require playlist for authenticated users
    if (isAuthenticated && !playlistId) {
      toast.error("No playlist selected");
      navigate("/web-player/login");

      return;
    }

    // IMPORTANT: Store playlistId in localStorage for heartbeat service (only if authenticated)
    if (isAuthenticated && playlistId) {
      localStorage.setItem("currentPlaylistId", playlistId);
    }

    // Ensure profile is stored (only if authenticated)
    if (isAuthenticated && profile?.id) {
      sessionStorage.setItem("selectedProfile", JSON.stringify(profile));
    }

    // Ensure profile token is set if we have a profile
    if (isAuthenticated && profile?.token) {
      sessionStorage.setItem(`profile_token_${playlistId}`, profile.token);
    }

    const loadHomeContent = async () => {
      try {
        setIsInitialLoad(true);

        const response = await apiService.webPlayerGetHome();

        if (!response) {
          console.error("[DEBUG] No response from /v2/home");
          setIsInitialLoad(false);

          return;
        }

        // Handle both { data: {...} } and direct response formats
        const data = response.data || response;

        debugLog("[DEBUG] /v2/home response data:", {
          hasContinueWatching: !!data.continueWatching,
          continueWatchingCount: data.continueWatching?.length || 0,
          hasCarousel: !!data.carousel,
          hasMovies: !!data.movies,
        });

        // Process carousel/featured content
        const carousel = data.carousel || data.featured || [];

        if (carousel && Array.isArray(carousel)) {
          const carouselData = carousel.map((item) => ({
            id: item.mediaId,
            mediaId: item.mediaId,
            tmdbId: item.tmdbId,
            title: item.name,
            backdrop: item.backdropPath,
            poster: item.posterPath,
            logo: item.logoPath,
            genre: item.genres,
            year: item.year,
            type: item.mediaType,
            mediaType: item.mediaType,
            description: item.description,
            rating: item.rating,
            rottenTomatoes: item.rottenTomatoes,
            duration: clockDuration(item.duration),
            isBookmarked: item.isBookmarked,
            progress:
              item.metadata?.progressPercent || item.progressPercent || 0,
          }));

          // Use carousel data directly - API already includes progress
          setCarouselItems(carouselData);
          sessionStorage.setItem("home_carousel", JSON.stringify(carouselData));
          // Series rows on /v2/home come back as 00:00:00. The episode
          // length is on the series detail, so fill those in after paint.
          void fillSeriesWatchTimes(carouselData, setCarouselItems);
        }

        // Process continue watching
        const continueWatchingData = data.continueWatching || [];

        debugLog(
          "[DEBUG] Continue watching from API - raw count:",
          continueWatchingData.length
        );
        debugLog(
          "[DEBUG] Continue watching from API - first 3 items:",
          continueWatchingData.slice(0, 3).map((i) => ({
            mediaId: i.mediaId,
            name: i.name,
            mediaType: i.mediaType,
            posterPath: i.posterPath,
            hasStreamUrl: !!i.metadata?.streamUrl,
          }))
        );

        if (
          continueWatchingData &&
          Array.isArray(continueWatchingData) &&
          continueWatchingData.length > 0
        ) {
          const mappedContinueWatching = continueWatchingData.map((item) => ({
            ...item,
            id: item.mediaId || item.id,
            stream_id: item.mediaId || item.stream_id,
            title: item.name || item.title,
            poster: item.posterPath || item.poster,
            posterPath: item.posterPath || item.poster,
            stream_icon: item.logoPath || item.posterPath || item.stream_icon,
            stream_url: item.metadata?.streamUrl || item.stream_url,
            type: item.mediaType || item.type,
            backdrop: item.backdropPath || item.backdrop,
            episodeInfo: item.metadata?.episodeInfo || item.episodeInfo,
            // Progress will be 0 initially, fetchAndApplyProgress will update it
            progress:
              item.metadata?.progressPercent || item.progressPercent || 0,
          }));

          debugLog(
            "[DEBUG] Setting continueWatching state with",
            mappedContinueWatching.length,
            "items"
          );

          setContinueWatching(mappedContinueWatching);
          sessionStorage.setItem(
            "home_continue_watching",
            JSON.stringify(mappedContinueWatching)
          );
        } else {
          debugLog("[DEBUG] No continue watching items from API");
        }

        // Process recently watched
        const watchedData = data.watched || [];

        if (watchedData && Array.isArray(watchedData)) {
          debugLog(
            "Loading watched content from v2/home endpoint:",
            watchedData
          );
          const mappedWatched = watchedData.slice(0, 10).map((item) => ({
            ...item,
            id: item.mediaId,
            stream_id: item.mediaId,
            name: item.name,
            title: item.name,
            logo: item.logoPath || item.posterPath,
            logoPath: item.logoPath || item.posterPath,
            stream_icon: item.logoPath || item.posterPath,
            stream_url: item.streamUrl,
            stream_type: item.mediaType === "live-tv" ? "live" : item.mediaType,
            type: item.mediaType,
            poster: item.posterPath,
            backdrop: item.backdropPath,
            year: item.year,
            genres: item.genres,
            rating: item.rating,
            isBookmarked: item.isBookmarked,
            categoryId: item.categoryId,
            epgChannelId: item.epgChannelId,
            // Process EPG/programme data
            currentProgram: item.programmes?.[0]?.title || item.currentProgram,
            epgTitle: item.programmes?.[0]?.title || item.epgTitle,
            nowPlaying: item.programmes?.[0]?.title || item.nowPlaying,
            programmes: item.programmes || [],
            progress: item.programmes?.[0]
              ? Math.round(
                  ((Date.now() - item.programmes[0].start) /
                    (item.programmes[0].stop - item.programmes[0].start)) *
                    100
                )
              : (item.progressPercent ??
                item.metadata?.progressPercent ??
                item.progress ??
                0),
            watched: item.watched || item.isWatched || false,
          }));

          // Use data directly - API already includes progress
          setRecentlyWatched(mappedWatched);
          sessionStorage.setItem(
            "home_recently_watched",
            JSON.stringify(mappedWatched)
          );
        } else if (data.recentlyWatched) {
          // Fallback to regular recentlyWatched field if no watched field
          const recentData =
            data.recentlyWatched.data ||
            data.recentlyWatched.items ||
            data.recentlyWatched;

          if (Array.isArray(recentData)) {
            const mappedRecent = recentData.slice(0, 10).map((item) => {
              const logoUrl =
                item.logoPath ||
                item.logo_path ||
                item.logo ||
                item.channelLogo ||
                item.channel_logo ||
                item.stream_icon ||
                item.streamIcon ||
                item.icon ||
                item.image;

              return {
                ...item,
                logo: logoUrl,
                logoPath: logoUrl,
                stream_icon: logoUrl,
                stream_url: item.streamUrl || item.stream_url || item.url,
                stream_id: item.stream_id || item.id,
                // Preserve EPG data
                currentProgram:
                  item.programmes?.[0]?.title ||
                  item.currentProgram ||
                  item.current_program ||
                  item.epg_title,
                epgTitle:
                  item.programmes?.[0]?.title ||
                  item.epgTitle ||
                  item.epg_title ||
                  item.nowPlaying ||
                  item.now_playing,
                nowPlaying:
                  item.programmes?.[0]?.title ||
                  item.nowPlaying ||
                  item.now_playing ||
                  item.epgTitle,
                progress:
                  item.progress ||
                  item.watchProgress ||
                  item.watch_progress ||
                  (item.programmes?.[0]
                    ? Math.round(
                        ((Date.now() - item.programmes[0].start) /
                          (item.programmes[0].stop -
                            item.programmes[0].start)) *
                          100
                      )
                    : 0),
                programmes: item.programmes || item.programs || [],
              };
            });

            // Use data directly - API already includes progress
            setRecentlyWatched(mappedRecent);
            sessionStorage.setItem(
              "home_recently_watched",
              JSON.stringify(mappedRecent)
            );
          }
        }

        // Process movies
        const moviesData = data.movies || [];

        if (Array.isArray(moviesData)) {
          const mappedMovies = moviesData.slice(0, 15).map((movie) => ({
            id: movie.mediaId || movie.tmdbId || movie.id,
            mediaId: movie.mediaId || movie.id,
            tmdbId: movie.tmdbId,
            title: movie.name || movie.title,
            poster: movie.posterPath || movie.poster || movie.thumbnail,
            backdrop: movie.backdropPath || movie.posterPath || movie.backdrop,
            genre: movie.genres || movie.genre || "Action",
            year: movie.year || "2024",
            type: movie.mediaType || "movie",
            mediaType: movie.mediaType || "movie",
            rating: movie.rating || movie.rating_5based,
            rating_5based: movie.rating_5based,
            imdbRating: movie.imdbRating,
            description: movie.description || movie.plot || "",
            duration: movie.duration || movie.runtime || "2h",
            logoPath: movie.logoPath,
            rottenTomatoes: movie.rottenTomatoes,
            isBookmarked: movie.isBookmarked || false,
            // Watch progress (Samsung/Android: from item.progressPercent or
            // metadata.progressPercent on /v2/home response).
            progress:
              movie.progressPercent ?? movie.metadata?.progressPercent ?? 0,
            watched: movie.watched || movie.isWatched || false,
          }));

          // Use data directly - API already includes any progress
          setMovies(mappedMovies);
          sessionStorage.setItem("home_movies", JSON.stringify(mappedMovies));
        }

        // Process series
        const seriesData = data.series || [];

        if (Array.isArray(seriesData)) {
          const mappedSeries = seriesData.slice(0, 15).map((series) => ({
            id: series.mediaId || series.tmdbId || series.id,
            mediaId: series.mediaId || series.id,
            tmdbId: series.tmdbId,
            title: series.name || series.title,
            poster: series.posterPath || series.poster || series.thumbnail,
            backdrop:
              series.backdropPath || series.posterPath || series.backdrop,
            genre: series.genres || series.genre || "Drama",
            year: series.year || "2024",
            type: series.mediaType || "series",
            mediaType: series.mediaType || "series",
            rating: series.rating || series.rating_5based,
            rating_5based: series.rating_5based,
            imdbRating: series.imdbRating,
            description: series.description || series.plot || "",
            episodes: series.episode_count || series.episodes,
            seasons: series.season_count || series.seasons,
            logoPath: series.logoPath,
            rottenTomatoes: series.rottenTomatoes,
            isBookmarked: series.isBookmarked || false,
            // Watch progress for series cards.
            progress:
              series.progressPercent ?? series.metadata?.progressPercent ?? 0,
            watched: series.watched || series.isWatched || false,
          }));

          // Use data directly - API already includes any progress
          setSeries(mappedSeries);
          sessionStorage.setItem("home_series", JSON.stringify(mappedSeries));
        }

        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error loading home content:", error);
        setIsInitialLoad(false);
      }
    };

    // Call loadHomeContent on mount
    loadHomeContent();

    // Listen for heartbeat updates - ONLY for progress data
    // Note: Global HeartbeatManager handles starting/stopping the service
    const handleHeartbeatUpdate = (data) => {
      if (!data?.heartbeat?.data?.recentlyWatchedProgress) return;

      const progressData = data.heartbeat.data.recentlyWatchedProgress;

      if (!Array.isArray(progressData) || progressData.length === 0) return;

      // Merge heartbeat-fresh server data into localStorage WITHOUT clobbering
      // entries the player just saved locally. (Earlier code overwrote the
      // whole array — that wiped a player's save before the server's eventual
      // consistency caught up, killing resume on freshly watched items.)
      const storageKey = "webPlayer_recentlyWatchedProgress";
      const existingList = (() => {
        try {
          return JSON.parse(localStorage.getItem(storageKey) || "[]");
        } catch {
          return [];
        }
      })();

      const keyOf = (item) =>
        `${String(item.mediaId)}::${item.mediaType || "movie"}`;
      const merged = new Map();

      // Seed with whatever the player has saved locally (the freshest source
      // for items watched within the last few seconds).
      existingList.forEach((item) => {
        if (item && item.mediaId != null) merged.set(keyOf(item), item);
      });

      // Apply server entries — if the server's version is newer (higher
      // progress) trust it; otherwise keep the local copy.
      progressData.forEach((item) => {
        if (item == null || item.mediaId == null) return;
        const serverEntry = {
          mediaId: item.mediaId,
          mediaType: item.mediaType || "movie",
          tmdbId: item.tmdbId,
          progress: item.progress || 0,
          duration: item.duration || 0,
          progressPercent: item.progressPercent || 0,
          watched: item.watched || false,
          isValid: (item.progress || 0) > 10000,
        };
        const k = keyOf(serverEntry);
        const existing = merged.get(k);

        if (
          !existing ||
          (serverEntry.progress || 0) >= (existing.progress || 0)
        ) {
          merged.set(k, serverEntry);
        }
      });

      const progressList = Array.from(merged.values());

      localStorage.setItem(storageKey, JSON.stringify(progressList));

      // Apply progress to every home row — Samsung/Android source of truth is
      // the heartbeat's recentlyWatchedProgress, not a separate /progress endpoint.
      const progressByMediaId = {};
      const progressByTmdbId = {};
      const watchedSet = new Set();

      progressData.forEach((p) => {
        if (p.mediaId != null) {
          progressByMediaId[String(p.mediaId)] = p.progressPercent || 0;
          if (p.watched) watchedSet.add(String(p.mediaId));
        }
        if (p.tmdbId != null) {
          progressByTmdbId[String(p.tmdbId)] = p.progressPercent || 0;
          if (p.watched) watchedSet.add(String(p.tmdbId));
        }
      });

      const lookupProgress = (item) => {
        const ids = [item.mediaId, item.id, item.stream_id, item.tmdbId]
          .filter((x) => x != null)
          .map(String);

        for (const id of ids) {
          if (progressByMediaId[id] !== undefined) return progressByMediaId[id];
          if (progressByTmdbId[id] !== undefined) return progressByTmdbId[id];
        }

        return undefined;
      };

      const lookupWatched = (item) => {
        const ids = [item.mediaId, item.id, item.tmdbId]
          .filter((x) => x != null)
          .map(String);

        return ids.some((id) => watchedSet.has(id));
      };

      const enrich = (list) => {
        if (!list || list.length === 0) return list;
        let mutated = false;
        const next = list.map((item) => {
          const p = lookupProgress(item);
          const w = lookupWatched(item);

          if (p === undefined && !w) return item;
          if ((item.progress || 0) === (p || 0) && !!item.watched === w) {
            return item;
          }
          mutated = true;

          return {
            ...item,
            progress: p !== undefined ? p : item.progress,
            watched: w || item.watched,
          };
        });

        return mutated ? next : list;
      };

      setContinueWatching(enrich);
      setRecentlyWatched(enrich);
      setMovies(enrich);
      setSeries(enrich);
    };

    webPlayerHeartbeatService.addListener(handleHeartbeatUpdate);

    // Apply whatever the heartbeat service already has cached so progress shows
    // immediately on mount instead of waiting up to 5s for the next tick. Then
    // force one fresh tick (refreshes /v2/heartbeat — same endpoint Samsung's
    // refreshContinueWatching uses to pull in the just-watched item).
    const last = webPlayerHeartbeatService.getLastData();

    if (last) handleHeartbeatUpdate(last);
    if (typeof webPlayerHeartbeatService.sendHeartbeat === "function") {
      webPlayerHeartbeatService.sendHeartbeat();
    }

    return () => {
      // Only remove our listener - global HeartbeatManager handles start/stop
      webPlayerHeartbeatService.removeListener(handleHeartbeatUpdate);
    };
    // Include location.key to re-fetch when navigating back to home page
  }, [playlistId, navigate, profile, location.key, isAuthenticated]);

  // (Progress enrichment is handled by the heartbeat listener above —
  //  Samsung/Android source it from heartbeat.recentlyWatchedProgress, not
  //  from a dedicated /progress endpoint.)

  // Handle removing item from recently watched
  const handleItemRemove = useCallback((item) => {
    const itemId = item.id || item.stream_id;

    // Remove the item from state after animation completes
    // The RecentlyWatchedCarousel handles the animation and timing
    setRecentlyWatched((prev) => {
      const updated = prev.filter((i) => (i.id || i.stream_id) !== itemId);

      sessionStorage.setItem("home_recently_watched", JSON.stringify(updated));

      return updated;
    });
  }, []);

  // Handle removing item from continue watching
  const handleContinueWatchingRemove = useCallback((item) => {
    const itemId = item.mediaId || item.id;

    // Remove the item from state after animation completes
    setContinueWatching((prev) => {
      const updated = prev.filter((i) => (i.mediaId || i.id) !== itemId);

      sessionStorage.setItem("home_continue_watching", JSON.stringify(updated));

      return updated;
    });
  }, []);

  // Handle beta banner dismissal
  const handleDismissBetaBanner = () => {
    sessionStorage.setItem("betaBannerDismissed", "true");
    setShowBetaBanner(false);
  };

  // Handle content item clicks
  const handleContentClick = useCallback(
    async (item) => {
      // Build stream URL based on item type
      let streamUrl = null;

      // First priority: Use the streamUrl directly from the API
      if (item.stream_url || item.streamUrl) {
        const encodedUrl = item.stream_url || item.streamUrl;

        // Decode Base64 encoded URLs if needed
        if (encodedUrl && !encodedUrl.startsWith("http")) {
          try {
            streamUrl = atob(encodedUrl);
          } catch (e) {
            // If decoding fails, use as-is (might not be encoded)
            streamUrl = encodedUrl;
          }
        } else {
          streamUrl = encodedUrl;
        }
      }

      // If no streamUrl yet, try to construct it
      if (!streamUrl) {
        // For recently watched items, construct URL using playlist data
        if (item.stream_id || item.id) {
          if (playlist?.url && playlist?.username && playlist?.password) {
            // Use playlist URL format for live streams
            const baseUrl = playlist.url.replace(/\/$/, ""); // Remove trailing slash
            const streamId = item.stream_id || item.id;

            streamUrl = `${baseUrl}/${playlist.username}/${playlist.password}/${streamId}`;
          } else if (playlist?.uriHost) {
            // Fallback to uriHost format
            const baseUrl = playlist.uriHost.startsWith("http")
              ? playlist.uriHost
              : `https://${playlist.uriHost}`;

            if (item.type === "movie" || item.mediaType === "movie") {
              streamUrl = `${baseUrl}/movie/${item.mediaId || item.id}/play`;
            } else if (item.type === "series" || item.mediaType === "series") {
              streamUrl = `${baseUrl}/series/${item.mediaId || item.id}/play`;
            } else {
              // Default to live stream format
              streamUrl = `${baseUrl}/live/${playlist.username}/${playlist.password}/${item.stream_id || item.id}`;
            }
          }
        }
      }

      if (streamUrl) {
        debugLog("Playing stream with URL:", streamUrl);
        // Launch the video player exactly like watchlist does
        setCurrentPlayingItem(item);
        setPlayerOpen(true);
        setCurrentStreamUrl(streamUrl);
        setCurrentStreamTitle(item.name || item.title);
      } else {
        console.error("No stream URL available for item:", item);
      }
    },
    [playlist]
  );

  // Show loading spinner only for the very first load when there's no data
  if (
    isInitialLoad &&
    carouselItems.length === 0 &&
    movies.length === 0 &&
    series.length === 0 &&
    recentlyWatched.length === 0
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-white text-lg">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Menu Component */}
      <LeftMenu activeMenuItem="home" playlist={playlist} profile={profile} />

      {/* Mobile Menu */}
      <MobileMenu />

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Beta Banner - Center of Screen */}
        <AnimatePresence>
          {showBetaBanner && (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="bg-yellow-500/90 backdrop-blur-sm text-black px-6 py-3 rounded-full flex items-center gap-3 pointer-events-auto"
                style={{
                  boxShadow:
                    "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
                }}
              >
                <span className="text-xl">🐛</span>
                <span className="font-medium text-sm">
                  This is still in Beta, there will be bugs
                </span>
                <button
                  className="ml-2 text-black hover:text-gray-700 transition-colors"
                  onClick={handleDismissBetaBanner}
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dashboard Button - Bottom Right */}
        <button
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
          onClick={() => {
            // Clear all session data
            webPlayerStorage.clearAll();
            // Redirect to dashboard
            window.location.href = "https://dashboard.lunova.app";
          }}
        >
          Dashboard
        </button>

        {/* Weather Widget and Time - Mobile: compact weather only, Desktop: full widget + time */}
        <div
          className="absolute top-4 right-4 sm:right-8 md:right-12 lg:right-16 xl:right-20 flex items-center gap-2 whitespace-nowrap"
          style={{
            zIndex: 99999,
          }}
        >
          {/* Mobile Portrait: Compact widget */}
          <div className="block landscape:hidden lg:hidden">
            <WeatherWidget compact={true} />
          </div>
          {/* Mobile Landscape + Desktop: Full widget with time */}
          <div className="hidden landscape:flex lg:flex items-center gap-2">
            <WeatherWidget />
          </div>
        </div>

        {/* Main Content Container - Allow scrolling */}
        <div className="relative min-h-screen min-w-0 overflow-y-auto overflow-x-hidden">
          <style>
            {`
              /* Desktop short windows: pin the rows about 500px from the top. */
              .content-sections-container {
                margin-top: calc(500px - 100dvh);
              }
              /* iPhone: rows overlap the bottom 15% of the hero, and the first
                 card lines up with the title past the side rail. */
              @media (max-width: 1023px) {
                .content-sections-container {
                  margin-top: calc(-15dvh - 4rem);
                }
                .content-sections-container .section-content {
                  padding-left: 0;
                  padding-right: 0;
                  margin-bottom: 1rem;
                }
                .content-sections-container .section-content > h2 {
                  padding-bottom: 0.25rem;
                }
                .content-sections-container .section-content > h2,
                .content-sections-container .home-edge-row {
                  padding-left: max(5rem, calc(env(safe-area-inset-left, 0px) + 4rem));
                  padding-right: 15px;
                  box-sizing: border-box;
                }
                .content-sections-container .section-content > .py-4 {
                  padding-top: 0;
                  padding-bottom: 0;
                }
                .content-sections-container .home-edge-row {
                  width: 100% !important;
                  max-width: 100% !important;
                  margin-left: 0 !important;
                  padding-top: 4px !important;
                }
              }
              /* Desktop: rows run edge to edge. Titles and the first card clear the menu. */
              @media (min-width: 1024px) and (min-height: 900px) {
                .content-sections-container {
                  margin-top: -240px;
                }
              }
              @media (min-width: 1024px) {
                .content-sections-container {
                  margin-left: 0;
                  padding-left: 0;
                  padding-right: 0;
                }
                .content-sections-container .section-content {
                  padding-left: 0;
                  padding-right: 0;
                }
                .content-sections-container .section-content > h2,
                .content-sections-container .home-edge-row {
                  padding-left: calc(4rem + 30px);
                }
              }
              @media (min-width: 1280px) {
                .content-sections-container .section-content > h2,
                .content-sections-container .home-edge-row {
                  padding-left: calc(5rem + 30px);
                }
              }
            `}
          </style>
          {/* Edge-to-edge hero carousel — full screen height, sits behind fixed left rail */}
          <div className="relative z-20">
            <EmblaCarousel items={carouselItems} />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-[100dvh] h-[200px]"
              style={{
                background:
                  "linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0.75) 40%, transparent 100%)",
              }}
            />
          </div>

          {/* Content Sections — overlap into the hero. Matches Samsung
              app.css #contentRows { margin-top: -240px } where the cards
              show ~50% peek over the carousel. */}
          <div className="relative z-30 w-full min-w-0 content-sections-container">
            {/* Content wrapper */}
            <div className="w-full">
              {/* Continue Watching Section - Only show if authenticated and there are items with progress */}
              {isAuthenticated && continueWatching.length > 0 && (
                <ContinueWatchingSection
                  items={continueWatching}
                  onItemClick={handleContentClick}
                  onItemRemove={handleContinueWatchingRemove}
                  onViewDetails={(item) => {
                    const mediaType = item.mediaType || item.type || "movie";
                    const mediaId = item.mediaId || item.id;

                    if (mediaType === "movie") {
                      navigate(`/web-player/movie/${mediaId}`);
                    } else if (mediaType === "series") {
                      navigate(`/web-player/series/${mediaId}`);
                    }
                  }}
                />
              )}

              {/* Recently Watched Section - Only show if authenticated */}
              {isAuthenticated &&
                (() => {
                  const shouldShow = recentlyWatched.length > 0;

                  if (shouldShow) {
                    hasShownRecentlyWatched.current = true;
                  }

                  return hasShownRecentlyWatched.current;
                })() && (
                  <RecentlyWatchedSection
                    items={recentlyWatched}
                    onItemClick={handleContentClick}
                    onItemRemove={handleItemRemove}
                  />
                )}

              {/* Recently Added Series */}
              <section className="mb-8 overflow-visible section-content">
                <h2 className="text-xl sm:text-2xl font-semibold text-white pb-3.5">
                  Recently Added Series
                </h2>
                <div className="py-4" style={{ overflow: "visible" }}>
                  <SeriesCarousel
                    enableContextMenu
                    items={(series.length > 0
                      ? series
                      : carouselItems.filter(
                            (item) =>
                              item.type === "series" || item.type === "tv"
                          ).length > 0
                        ? carouselItems.filter(
                            (item) =>
                              item.type === "series" || item.type === "tv"
                          )
                        : carouselItems
                    ).slice(0, 15)}
                    seeAllHref="/web-player/series"
                  />
                </div>
              </section>

              {/* Recently Added Movies */}
              <section className="mb-8 overflow-visible section-content">
                <h2 className="text-xl sm:text-2xl font-semibold text-white pb-3.5">
                  Recently Added Movies
                </h2>
                <div className="py-4" style={{ overflow: "visible" }}>
                  <MoviesCarousel
                    enableContextMenu
                    items={(movies.length > 0
                      ? movies
                      : carouselItems.filter((item) => item.type === "movie")
                            .length > 0
                        ? carouselItems.filter((item) => item.type === "movie")
                        : carouselItems
                    ).slice(0, 15)}
                    seeAllHref="/web-player/movies"
                  />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Video Player Modal - exactly like watchlist */}
      {playerOpen && (
        <PlyrVideoPlayer
          episodeId={(() => {
            // For series, extract episodeId from compound mediaId (seriesId-episodeId)
            const mediaType =
              currentPlayingItem?.type ||
              currentPlayingItem?.mediaType ||
              "movie";

            if (
              mediaType === "series" ||
              mediaType === "tv" ||
              mediaType === "episode"
            ) {
              const mediaId = String(
                currentPlayingItem?.mediaId || currentPlayingItem?.id || ""
              );

              if (mediaId.includes("-")) {
                return mediaId.split("-")[1];
              }
            }

            return undefined;
          })()}
          isOpen={playerOpen}
          mediaId={currentPlayingItem?.mediaId || currentPlayingItem?.id}
          mediaType={
            currentPlayingItem?.type || currentPlayingItem?.mediaType || "movie"
          }
          seriesId={(() => {
            // For series, extract seriesId from compound mediaId (seriesId-episodeId)
            const mediaType =
              currentPlayingItem?.type ||
              currentPlayingItem?.mediaType ||
              "movie";

            if (
              mediaType === "series" ||
              mediaType === "tv" ||
              mediaType === "episode"
            ) {
              const mediaId = String(
                currentPlayingItem?.mediaId || currentPlayingItem?.id || ""
              );

              if (mediaId.includes("-")) {
                return mediaId.split("-")[0];
              }

              return mediaId;
            }

            return undefined;
          })()}
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={() => {
            setPlayerOpen(false);
            setCurrentStreamUrl(null);
            setCurrentStreamTitle(null);
            setCurrentPlayingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(WebPlayerHome);
